import React, { useEffect, useState, useMemo, useRef } from "react";
import { Pagination } from "./Pagination";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import {
    ShieldCheck,
    Search,
    RefreshCw,
    ChevronUp,
    ChevronDown,
    ShieldAlert,
    ArrowUpCircle,
    AlertTriangle,
} from "lucide-react";

interface PatchListProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const DEFAULT_PER_PAGE = 10;

const PatchList: React.FC<PatchListProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [patches, setPatches] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Tab state & compliance states
    const [activeTab, setActiveTab] = useState<"system" | "available">("system");
    const [complianceData, setComplianceData] = useState<any | null>(null);
    const [loadingCompliance, setLoadingCompliance] = useState(false);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    const fetchPatches = async (
        skip: number = 0,
        limit: number = perPage,
        searchTerm: string = search,
        currentSort = sortConfig
    ) => {
        setLoading(true);
        try {
            const queryObj: any = { client_id, organization_id, agent_id, asset_id };
            if (searchTerm.trim()) {
                const term = searchTerm.trim();
                queryObj.$or = [
                    { hotfix_id: { $ilike: `%${term}%` } },
                    { original_hotfix_id: { $ilike: `%${term}%` } },
                    { description: { $ilike: `%${term}%` } },
                    { installed_by: { $ilike: `%${term}%` } },
                    { installed_on: { $ilike: `%${term}%` } },
                ];
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj),
            };

            // Add API Sorting if requested
            if (currentSort) {
                params.sort_by = currentSort.key;
                params.sort_order = currentSort.direction;
            }

            const response = await baseapi.get("/asset_patches", { params });
            setPatches(response.data?.data ?? []);
            setTotal(response.data?.total ?? 0);
        } catch (err: any) {
            showError(err.message);
            setPatches([]);
            setTotal(0);
        }
        setLoading(false);
    };

    const fetchCompliance = async () => {
        setLoadingCompliance(true);
        try {
            const response = await baseapi.get("/asset_windows_kb_compliance", {
                params: { query: JSON.stringify({ asset_id }) }
            });
            if (response.data?.status && response.data?.data?.length > 0) {
                setComplianceData(response.data.data[0]);
            } else {
                setComplianceData(null);
                setActiveTab("system");
            }
        } catch (err: any) {
            console.error("Error fetching windows compliance", err);
            setComplianceData(null);
            setActiveTab("system");
        } finally {
            setLoadingCompliance(false);
        }
    };

    useEffect(() => {
        setActiveTab("system");
        fetchPatches(0, perPage, "", sortConfig);
        fetchCompliance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            const trimmed = value.trim();
            setSearch(trimmed);
            setPage(1);
            fetchPatches(0, perPage, trimmed, sortConfig);
        }, 400);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            const trimmedSearch = searchInput.trim();
            setSearch(trimmedSearch);
            setPage(1);
            fetchPatches(0, perPage, trimmedSearch, sortConfig);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / perPage)) {
            setPage(newPage);
            fetchPatches((perPage * (newPage - 1)), perPage, search, sortConfig);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        fetchPatches(0, value, search, sortConfig);
    };

    const handleRefresh = () => {
        const newSearch = "";
        setSearch(newSearch);
        setSearchInput("");
        fetchPatches((page - 1) * perPage, perPage, newSearch, sortConfig);
    };

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        const newSortConfig = { key, direction: newDirection };
        setSortConfig(newSortConfig);
        setPage(1); // Reset to page 1 on sort
        fetchPatches(0, perPage, search, newSortConfig);
    };

    // Parse recommended_cu JSON string safely
    const parsedRecommendedCU = useMemo(() => {
        if (!complianceData?.recommended_cu) return null;
        try {
            return typeof complianceData.recommended_cu === "string"
                ? JSON.parse(complianceData.recommended_cu)
                : complianceData.recommended_cu;
        } catch (e) {
            console.error("Error parsing recommended_cu", e);
            return null;
        }
    }, [complianceData]);

    // Parse update_summary JSON string safely
    const parsedUpdateSummary = useMemo(() => {
        if (!complianceData?.update_summary) return null;
        try {
            return typeof complianceData.update_summary === "string"
                ? JSON.parse(complianceData.update_summary)
                : complianceData.update_summary;
        } catch (e) {
            console.error("Error parsing update_summary", e);
            return null;
        }
    }, [complianceData]);

    // Parse missing_kbs JSON string safely
    const parsedMissingKBs: any[] = useMemo(() => {
        if (!complianceData?.missing_kbs) return [];
        try {
            const data = typeof complianceData.missing_kbs === "string"
                ? JSON.parse(complianceData.missing_kbs)
                : complianceData.missing_kbs;
            return Array.isArray(data) ? data : [];
        } catch (e) {
            console.error("Error parsing missing_kbs", e);
            return [];
        }
    }, [complianceData]);



    // Helper for formatting KB string safely
    const formatKb = (kb: string) => {
        if (!kb) return "-";
        return kb.toUpperCase().startsWith("KB") ? kb.toUpperCase() : `KB${kb}`;
    };

    // Helper for Microsoft Support URL
    const getMicrosoftKbUrl = (kb: string) => {
        if (!kb) return "#";
        const cleanKb = kb.replace(/[^0-9]/g, "");
        return `https://support.microsoft.com/help/${cleanKb}`;
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass }: { label: string; sortKey: string; widthClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 cursor-pointer group select-none`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-green-400" />
                            ) : (
                                <ChevronDown size={12} className="text-green-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    return (
        <div className="p-3">
            {/* Compact Header with Tabs */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">
                            {activeTab === "system" ? "System Patches" : "Available Updates"}
                        </h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {activeTab === "system" ? `${total || '...'} records found` : "Compliance Summary"}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 self-end sm:self-auto">
                    {/* Inner Tabs Toggle */}
                    {complianceData && (
                        <div className="flex bg-gray-900/80 border border-gray-700 rounded-md p-1">
                            <button
                                onClick={() => setActiveTab("system")}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "system"
                                    ? "bg-gray-700 text-white shadow-sm"
                                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
                                    }`}
                            >
                                System Patches
                            </button>
                            <button
                                onClick={() => setActiveTab("available")}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "available"
                                    ? "bg-gray-700/80 text-green-400 border border-green-500/20 shadow-sm"
                                    : "text-green-500 hover:text-green-400 hover:bg-gray-800/50"
                                    }`}
                            >
                                <span className="relative flex h-1.5 w-1.5 mr-0.5 shrink-0">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                                </span>
                                <span className="animate-pulse">Available Updates</span>
                            </button>
                        </div>
                    )}

                    {activeTab === "system" && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                                title="Refresh data"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                            </button>
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                <input
                                    value={searchInput}
                                    onChange={handleSearchChange}
                                    onKeyDown={handleSearchKeyDown}
                                    placeholder="Search by KB..."
                                    className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {activeTab === "system" ? (
                /* Table Container */
                <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden">
                    <table className="w-full table-fixed">
                        <thead className="bg-gray-800/80">
                            <tr>
                                <SortableHeader widthClass="w-[20%]" label="KB" sortKey="hotfix_id" />
                                <SortableHeader widthClass="w-[40%]" label="Description" sortKey="description" />
                                <SortableHeader widthClass="w-[20%]" label="Installed On" sortKey="installed_on" />
                                <SortableHeader widthClass="w-[20%]" label="Installed By" sortKey="installed_by" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="py-8 text-center text-gray-200 text-[11px]">Loading patches...</td>
                                </tr>
                            ) : patches.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="py-8 text-center text-gray-200 text-[11px]">No patches found</td>
                                </tr>
                            ) : (
                                patches.map((p, idx) => (
                                    <tr key={p.id ?? idx} className="hover:bg-gray-800/40 transition-colors duration-100">
                                        <td className="px-3 py-2 text-[11px] text-white font-mono truncate" title={p.hotfix_id}>
                                            {p.caption ? (
                                                <a
                                                    href={p.caption}
                                                    className="text-blue-400 hover:text-blue-300 text-[10px] font-bold underline decoration-blue-400/30 underline-offset-2"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {p.hotfix_id}
                                                </a>
                                            ) : (
                                                <span className="text-gray-200 text-[10px]">{p.hotfix_id}</span>
                                            )}
                                        </td>

                                        <td className="px-3 py-2 text-[11px] text-gray-300 truncate" title={p.description}>{p.description}</td>
                                        <td className="px-3 py-2 text-[10px] text-gray-200 truncate">{p.installed_on}</td>
                                        <td className="px-3 py-2 text-[10px] text-gray-200 truncate" title={p.installed_by}>{p.installed_by}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot className="bg-gray-900/60">
                            <tr>
                                <td colSpan={4} className="p-0">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={Math.max(1, Math.ceil(total / perPage))}
                                        onPageChange={handlePageChange}
                                        itemsPerPage={perPage}
                                        totalItems={total}
                                        onItemsPerPageChange={handleItemsPerPageChange}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            ) : (
                /* Available Updates View */
                loadingCompliance ? (
                    <div className="bg-gray-900/40 rounded-md border border-gray-700 p-8 flex flex-col items-center justify-center min-h-[200px]">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-3"></div>
                        <p className="text-gray-200 text-xs font-semibold uppercase tracking-wider">Loading compliance data...</p>
                    </div>
                ) : complianceData ? (
                    <div className="space-y-4 animate-fade-in text-slate-100">
                        {/* Status Alert Banner */}
                        <div className={`flex items-start gap-3 p-3.5 rounded-lg border ${complianceData.compliance_status?.toLowerCase() === 'behind'
                            ? 'bg-red-500/10 border-red-500/20 text-red-200'
                            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-200'
                            }`}>
                            <div className="p-1 rounded-md bg-white/10 shrink-0 mt-0.5">
                                <ShieldAlert className={`w-4 h-4 ${complianceData.compliance_status?.toLowerCase() === 'behind' ? 'text-red-400' : 'text-emerald-400'
                                    }`} />
                            </div>
                            <div className="flex-1 text-[11px] leading-relaxed">
                                <span className="font-bold">
                                    {complianceData.compliance_status?.toLowerCase() === 'behind'
                                        ? `Attention: This asset is currently behind by ${complianceData.days_behind ?? "-"} days.`
                                        : 'Asset is fully compliant and up to date.'
                                    }
                                </span>
                                <span className="ml-1 text-gray-300">
                                    {complianceData.compliance_status?.toLowerCase() === 'behind'
                                        ? 'Applying the recommended Cumulative Update is advised to keep the asset compliant.'
                                        : 'No additional updates are recommended at this time.'
                                    }
                                </span>
                            </div>
                        </div>

                        {/* Detail Cards Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Card 1: Compliance & Latest Installed Status */}
                            <div className="bg-gray-900/40 border border-gray-700/80 rounded-lg p-4 flex flex-col justify-between shadow-md">
                                <div>
                                    <div className="flex items-center gap-2 mb-3 border-b border-gray-800 pb-2">
                                        <ShieldCheck className="w-4 h-4 text-orange-500" />
                                        <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">Compliance & Installed Patch Status</h3>
                                    </div>

                                    {/* Compliance Sub-section */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] uppercase font-bold text-gray-400">Compliance Status</span>
                                            <span className={`inline-block text-[9px] font-black uppercase px-2 py-0.5 rounded border ${complianceData.compliance_status?.toLowerCase() === 'behind'
                                                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                                }`}>
                                                {complianceData.compliance_status || "-"}
                                            </span>
                                        </div>
                                        <div className="mt-2.5 space-y-2">
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span className="text-gray-200">Days Behind</span>
                                                <span className="font-mono text-white font-bold">{complianceData.days_behind ?? "-"} days</span>
                                            </div>
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span className="text-gray-200">Patches Behind</span>
                                                <span className="font-mono text-white font-bold">{parsedUpdateSummary?.patches_behind ?? "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-800/60 my-3"></div>

                                    {/* Latest Installed KB Sub-section */}
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] uppercase font-bold text-gray-400">Installed KB</span>
                                            <span className="text-xs font-mono font-bold text-white bg-gray-800/80 px-2 py-0.5 rounded border border-gray-700/50">
                                                {complianceData.latest_installed_kb || "-"}
                                            </span>
                                        </div>
                                        <div className="mt-2.5 space-y-2">
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span className="text-gray-200">Release Date</span>
                                                <span className="font-mono text-white font-bold">
                                                    {complianceData.latest_installed_date
                                                        ? new Date(complianceData.latest_installed_date).toLocaleDateString("en-GB")
                                                        : "-"}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span className="text-gray-200">Installed On</span>
                                                <span className="font-mono text-white font-bold">{complianceData.latest_installed_on || "-"}</span>
                                            </div>
                                            {complianceData.latest_installed_note && (
                                                <div className="mt-2 pt-2 border-t border-gray-800 text-[10px] text-gray-300 leading-relaxed italic bg-gray-900/20 p-2 rounded">
                                                    {complianceData.latest_installed_note}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Recommended Cumulative Update */}
                            <div className="bg-gray-900/40 border border-gray-700/80 rounded-lg p-4 flex flex-col justify-between shadow-md">
                                <div>
                                    <div className="flex items-center gap-2 mb-3 border-b border-gray-800 pb-2">
                                        <ArrowUpCircle className="w-4 h-4 text-orange-500" />
                                        <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">Recommended CU</h3>
                                    </div>
                                    <div className="mt-2">
                                        {parsedRecommendedCU?.kb ? (
                                            <span className="text-sm font-mono font-bold text-blue-400">
                                                KB{parsedRecommendedCU.kb}
                                            </span>
                                        ) : (
                                            <span className="text-xs font-bold text-gray-200">None Recommended</span>
                                        )}
                                    </div>
                                    <div className="mt-4 space-y-2.5">
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-gray-200">Release Date</span>
                                            <span className="font-mono text-white font-bold">{parsedRecommendedCU?.date || "-"}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-gray-200">OS Build Target</span>
                                            <span className="font-mono text-white font-bold">{parsedRecommendedCU?.build || "-"}</span>
                                        </div>
                                        {parsedRecommendedCU?.note && (
                                            <div className="mt-3 pt-2 border-t border-gray-800 text-[10px] text-gray-300 leading-relaxed italic bg-gray-900/20 p-2 rounded">
                                                {parsedRecommendedCU.note}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Missing Cumulative Updates (missing_kbs) Section */}
                        <div className="bg-gray-900/40 border border-gray-700/80 rounded-lg p-4 shadow-md">
                            <div className="flex items-center justify-between mb-3 border-b border-gray-800 pb-2.5">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                                    <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">
                                        Missing Cumulative Updates
                                    </h3>
                                </div>
                                <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                    {parsedMissingKBs.length} Missing
                                </span>
                            </div>

                            {parsedMissingKBs.length > 0 ? (
                                <div className="overflow-x-auto rounded border border-gray-800">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-gray-800/80 text-[10px] uppercase font-bold text-gray-300">
                                            <tr>
                                                <th className="px-3.5 py-2.5 w-36">KB Article</th>
                                                <th className="px-3.5 py-2.5 w-36">Release Date</th>
                                                <th className="px-3.5 py-2.5">Update Description / Note</th>
                                                <th className="px-3.5 py-2.5 w-36 text-right">Target Build</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-800/60 text-xs">
                                            {parsedMissingKBs.map((mkb: any, idx: number) => {
                                                const formattedKbStr = formatKb(mkb.kb);
                                                const kbUrl = getMicrosoftKbUrl(mkb.kb);
                                                return (
                                                    <tr key={idx} className="hover:bg-gray-800/40 transition-colors">
                                                        <td className="px-3.5 py-2.5 font-mono font-bold text-[11px]">
                                                            <a
                                                                href={kbUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-amber-400 hover:text-amber-300 underline decoration-amber-400/30 underline-offset-2"
                                                            >
                                                                {formattedKbStr}
                                                            </a>
                                                        </td>
                                                        <td className="px-3.5 py-2.5 font-mono text-gray-300 text-[11px]">
                                                            {mkb.date || "-"}
                                                        </td>
                                                        <td className="px-3.5 py-2.5 text-gray-300 text-[11px] leading-relaxed">
                                                            {mkb.note || "-"}
                                                        </td>
                                                        <td className="px-3.5 py-2.5 font-mono text-gray-400 text-[11px] text-right">
                                                            {mkb.build || "-"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="py-6 text-center text-xs text-gray-400 bg-gray-950/20 rounded border border-gray-800/50">
                                    No missing cumulative updates identified.
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bg-gray-900/40 rounded-md border border-gray-700 py-8 text-center text-gray-200 text-[11px]">
                        No compliance data available
                    </div>
                )
            )}
        </div>
    );
};

export default PatchList;